import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MatchScoreDisplay } from "@/components/ui/MatchScoreDisplay";
import { RecommendationBadge } from "@/components/ui/RecommendationBadge";
import { 
  MessageSquare,
  Eye,
  MoreHorizontal
} from "lucide-react";
import { getMatchScore } from "@/lib/jobDetailsUtils";
import { AdminReferralWithUI } from "@/api/referrals";

interface KanbanReferralCardItemProps {
  referral: AdminReferralWithUI;
  onViewReferral: (referral: AdminReferralWithUI) => void;
  onDragStart?: (e: React.DragEvent, referralId: string) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  className?: string;
  showActions?: boolean;
  showMatchScore?: boolean;
  showNotes?: boolean;
  showReferrer?: boolean;
  showDate?: boolean;
}

export function KanbanReferralCardItem({
  referral,
  onViewReferral,
  onDragStart,
  onDragEnd,
  isDragging = false,
  className = "",
  showActions = true,
  showMatchScore = true,
  showNotes = true,
  showReferrer = true,
  showDate = true
}: KanbanReferralCardItemProps) {
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-4 border-l-red-500';
      case 'normal': return 'border-l-4 border-l-blue-500';
      case 'low': return 'border-l-4 border-l-gray-500';
      default: return 'border-l-4 border-l-gray-500';
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (onDragStart) {
      onDragStart(e, referral.id);
    }
  };

  return (
    <Card
      className={`border-0 shadow-sm cursor-move hover:shadow-lg transition-all duration-200 ease-out ${getPriorityColor(referral.priority)} ${
        isDragging 
          ? 'opacity-50 scale-95 rotate-2 shadow-2xl z-50' 
          : 'hover:scale-[1.02] hover:-translate-y-1'
      } ${className}`}
      draggable={!!onDragStart}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      data-testid={`referral-card-${referral.id}`}
    >
      <CardContent className="p-4">
        {/* Header: Candidate Name */}
        <div className="mb-3">
          <h4 className="font-medium text-gray-900 space-grotesk text-sm">
            {referral.candidateName}
          </h4>
        </div>

        {/* Job Title and Referrer */}
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-900 space-grotesk mb-1">
            {referral.jobTitle}
          </p>
          {showReferrer && (
            <p className="text-xs text-gray-500 space-grotesk">
              Referred by {referral.referrerName}
            </p>
          )}
        </div>

        {/* AI Recommendation and Match Score */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="h-3 w-3 text-gray-500" />
            <span className="text-xs text-gray-500 font-medium">AI Recommendation</span>
          </div>
          <div className="flex items-center justify-between">
            {showNotes && (
              <RecommendationBadge
                recommendation={referral.recommendation}
                size="sm"
              />
            )}
            {showMatchScore && (
              <MatchScoreDisplay
                matchScore={getMatchScore(referral)}
                recommendation={referral.recommendation}
                size="sm"
                variant="target"
              />
            )}
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewReferral(referral)}
              className="h-7 px-2 text-xs space-grotesk flex-1"
              data-testid={`button-view-referral-${referral.id}`}
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
            <Button
              variant="ghost" 
              size="sm"
              className="h-7 px-2"
              data-testid={`button-more-referral-${referral.id}`}
            >
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
